import { useEffect, useRef, useState } from "react";
import Sidebar from "../components/Sidebar";
import { useLocation } from "react-router";
import { ICONS } from "../constants/icons";
import { useUser } from "../contexts/UserContext";
import { FiCamera, FiX } from "react-icons/fi";
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { ref, update } from "firebase/database";
import { database, storage } from "../constants/firebase";
import { ROUTES } from "../App";
import useMoveTo from "../constants/navigation";
import MobileHeader from "../components/MobileHeader";
import { resizeImage, uploadFileToBunny } from "../utils/utils";
import ImageCropModal from "../components/ImageCropModal";
import Select from 'react-select';
import { getNames } from "country-list";


const customSelectStyles = {
    control: (provided, state) => ({
        ...provided,
        borderRadius: '0.75rem', // rounded-xl
        padding: '0.75rem 1rem', // px-4 py-3
        backgroundColor: '#F5F9FE', // same as bg
        borderColor: state.isFocused ? '#E5E7EB' : '#E5E7EB', // gray-200 border
        boxShadow: 'none',
        minHeight: '48px',
        cursor: 'pointer',
    }),
    valueContainer: (provided) => ({
        ...provided,
        padding: 0,
    }),
    input: (provided) => ({
        ...provided,
        margin: 0,
        padding: 0,
        color: '#000',
    }),
    placeholder: (provided) => ({
        ...provided,
        color: '#7C8BA0', // placeholder color
    }),
    dropdownIndicator: (provided) => ({
        ...provided,
        padding: '0 0rem',
        color: '#000',
    }),
    indicatorSeparator: () => ({
        display: 'none',
    }),
    menu: (provided) => ({
        ...provided,
        backgroundColor: '#F5F9FE', // same background as select
        borderRadius: '0.75rem',
        marginTop: '4px',
        border: '1px solid #E5E7EB', // same border color
        zIndex: 50,
        overflow: 'hidden',
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isFocused ? '#E5E7EB' : '#F5F9FE', // hover color same as input border hover
        color: '#000',
        cursor: 'pointer',
        padding: '0.75rem 1rem',
    }),
};

const EditProfile = () => {
    const navigate = useMoveTo();
    const location = useLocation();
    const { currentUser, refreshUser } = useUser();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isNewGoogleUser, setIsNewGoogleUser] = useState(false);
    const [tempImage, setTempImage] = useState(null);
    const [showCropModal, setShowCropModal] = useState(false);
    const [form, setForm] = useState({
        name: "",
        about: "",
        bio: "",
        website: "",
        niche: "",
        country: "",
        video: null,
        profilePic: null,
        portfolioBanner: null
    });

    const profileInputRef = useRef(null);
    const videoInputRef = useRef(null);
    const portfolioInputRef = useRef(null);
    const [countryOptions, setCountryOptions] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(0);


    useEffect(() => {
        const countries = getNames().map(name => ({ value: name, label: name }));
        setCountryOptions(countries);
    }, []);



    // Check for new Google user state
    useEffect(() => {
        if (location.state?.isNewGoogleUser) {
            setIsNewGoogleUser(true);
        }
    }, []);

    useEffect(() => {
        if (currentUser) {
            setForm((prevForm) => ({
                ...prevForm,
                name: currentUser.name || "",
                bio: currentUser.bio || "",
                about: currentUser.about || "",
                website: currentUser.website || "",
                niche: currentUser.niche || "",
                country: currentUser.country || "",
            }));
        }
    }, [currentUser]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === "bio") {
            const words = value.split(/\s+/);
            if (words.length > 100) return; // Stop if exceeds 100 words
        }
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleProfileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            const resizedBlob = await resizeImage(file, 200); // 200px x 200px
            setForm({ ...form, profilePic: resizedBlob });
        } catch (err) {
            console.error("Image resize failed:", err);
        }
    };

    const triggerProfileUpload = () => {
        profileInputRef.current?.click();
    };

    const triggerVideoUpload = () => {
        videoInputRef.current?.click();
    };

    const triggerPortfolioUpload = () => {
        portfolioInputRef.current?.click();
    };


    const handleSave = async () => {
        if (!currentUser) return;

        setIsSubmitting(true);

        try {
            const uid = currentUser.uid;
            const updates = {
                name: form.name,
                bio: form.bio,
                about: form.about,
                website: form.website,
                niche: form.niche,
                country: form.country,
                requiresProfileCompletion: null
            };

            // Upload profile picture
            if (form.profilePic) {
                const picRef = storageRef(storage, `users/${uid}/profile.jpg`);
                await uploadBytes(picRef, form.profilePic);
                const picURL = await getDownloadURL(picRef);
                updates.profilePic = picURL;
            }

            // Upload video
            // if (form.video) {
            //     const videoRef = storageRef(storage, `users/${uid}/showreel.mp4`);
            //     await uploadBytes(videoRef, form.video);
            //     const videoURL = await getDownloadURL(videoRef);
            //     updates.video = videoURL;
            //     updates.portfolioBanner = null;
            // }
            if (form.video) {
                const bunnyResult = await uploadFileToBunny(form.video, `showreels_${uid}`, "showreel", (percent) => setUploadProgress(percent));
                if (bunnyResult.success) {
                    const playbackUrl = bunnyResult.playbackUrl;
                    updates.video = playbackUrl; // Save playback URL in Firebase DB
                    updates.videoOrientation = bunnyResult.videoOrientation;
                    updates.videoWidth = bunnyResult.videoWidth;
                    updates.videoHeight = bunnyResult.videoHeight;
                    updates.portfolioBanner = null;
                } else {
                    throw new Error("Video upload failed: " + bunnyResult.message);
                }
            }

            // Upload portfolio image
            if (form.portfolioBanner) {
                const bannerRef = storageRef(storage, `users/${uid}/portfolio/banner.jpg`);
                await uploadBytes(bannerRef, form.portfolioBanner);
                const bannerURL = await getDownloadURL(bannerRef);
                updates.portfolioBanner = bannerURL;
                updates.video = null;
                updates.videoOrientation = null;
                updates.videoWidth = null;
                updates.videoHeight = null;
            }

            // Save to DB
            const userRef = ref(database, `Users/${uid}`);
            await update(userRef, updates);

            await refreshUser();

            window.showToast("Profile updated successfully!", "success");
            navigate(ROUTES.PROFILE, { replace: true });
        } catch (err) {
            console.error("Error saving profile:", err);
            window.showToast("Failed to update profile", "error");
        } finally {
            setIsSubmitting(false); // ✅ Done
        }
    };

    // ✅ Delete video (from Bunny + Firebase)
    const handleDeleteVideo = async () => {
        if (!currentUser?.video) return;

        const uid = currentUser.uid;
        const API_KEY = "eb14ec16-2cad-49f4-bf70b2e1c519-3382-4efe";
        const LIBRARY_ID = "516391";

        try {
            setIsDeleting(true);

            // 1️⃣ Find and delete the user's showreel collection from Bunny
            const res = await fetch(`https://video.bunnycdn.com/library/${LIBRARY_ID}/collections`, {
                headers: { accept: "application/json", AccessKey: API_KEY },
            });
            const data = await res.json();
            const userCollection = data.items.find((col) => col.name === `showreels_${uid}`);

            if (userCollection) {
                await fetch(
                    `https://video.bunnycdn.com/library/${LIBRARY_ID}/collections/${userCollection.guid}`,
                    {
                        method: "DELETE",
                        headers: { accept: "application/json", AccessKey: API_KEY },
                    }
                );
                console.log("✅ Deleted Bunny showreel collection");
            }

            // 2️⃣ Remove video reference from Firebase Database
            const userRef = ref(database, `Users/${uid}`);
            await update(userRef, {
                video: null,
                videoOrientation: null,
                videoWidth: null,
                videoHeight: null,
            });

            // 3️⃣ Update local user state
            await refreshUser();

            window.showToast("Video deleted successfully!", "success");
        } catch (err) {
            console.error("❌ Error deleting video:", err);
            window.showToast("Failed to delete video", "error");
        } finally {
            setIsDeleting(false);
        }
    };

    // ✅ Delete portfolio banner (from Firebase Storage + Database)
    const handleDeletePortfolioBanner = async () => {
        if (!currentUser?.portfolioBanner) return;

        const uid = currentUser.uid;
        try {
            setIsDeleting(true);

            // 1️⃣ Delete from Firebase Storage
            const bannerPath = `users/${uid}/portfolio/banner.jpg`;
            const bannerStorageRef = storageRef(storage, bannerPath);
            await window.firebaseDeleteObject(bannerStorageRef); // we'll add helper below

            // 2️⃣ Remove reference from Firebase Database
            const userRef = ref(database, `Users/${uid}`);
            await update(userRef, { portfolioBanner: null });

            // 3️⃣ Refresh local user
            await refreshUser();

            window.showToast("Portfolio banner deleted successfully!", "success");
        } catch (err) {
            console.error("❌ Error deleting portfolio banner:", err);
            window.showToast("Failed to delete portfolio banner", "error");
        } finally {
            setIsDeleting(false);
        }
    };

    window.firebaseDeleteObject = async (ref) => {
        try {
            await deleteObject(ref);
        } catch (err) {
            if (err.code === "storage/object-not-found") {
                console.warn("⚠️ Banner already deleted from storage");
            } else {
                throw err;
            }
        }
    };

    const handleCroppedImage = (croppedBlob) => {
        setForm({ ...form, portfolioBanner: croppedBlob, video: null });
        setShowCropModal(false);
        setTempImage(null);
    };


    return (
        <div className="h-screen bg-white text-black dark:bg-[#1A1A1A] dark:text-white overflow-y-auto">
            <MobileHeader title={isNewGoogleUser ? 'Complete Your Profile' : 'Edit Profile'} isBackBtn={true} />

            <div className="flex">
                <Sidebar />

                <main className="flex-1 h-screen pt-5 pb-24 md:pb-20 px-6 overflow-y-auto bg-white text-black dark:bg-[#1A1A1A] dark:text-white">
                    <div className="flex items-center justify-between">
                        <button onClick={() => navigate(-1)} className="flex-shrink-0 md:block hidden">
                            <img src={ICONS.arrowBack} className="w-4 h-4 md:w-5 md:h-5 grayscale brightness-0 dark:invert" alt="Back" />
                        </button>
                        <h2 className="text-2xl md:text-3xl font-semibold hidden md:block">
                            {isNewGoogleUser ? 'Complete Your Profile' : 'Edit Profile'}
                        </h2>
                        <div />
                    </div>

                    {isNewGoogleUser && (
                        <div className="max-w-md mx-auto mt-4 p-4 bg-blue-900/20 rounded-lg border border-blue-500">
                            <p className="text-blue-300 text-sm">
                                <strong>Almost there!</strong>  You'll need to complete this information to use all features
                            </p>
                        </div>
                    )}

                    <div className="max-w-md mx-auto md:pt-10 pt-3 space-y-6">
                        {/* Profile Picture */}
                        <div className="flex justify-center">
                            <div className="relative inline-flex items-center justify-center">
                                <img
                                    src={
                                        form.profilePic
                                            ? URL.createObjectURL(form.profilePic)
                                            : currentUser?.profilePic || 'https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/avatar-1.webp?alt=media&token=2d65470d-2104-4787-a5ef-cbfceb8a63ef'
                                    }
                                    alt="Avatar"
                                    className="w-28 h-28 rounded-full object-cover"
                                />

                                <input
                                    ref={profileInputRef}
                                    type="file"
                                    accept="image/png, image/jpeg, image/jpg,image/webp, image/heic, image/heif"
                                    className="hidden"
                                    onChange={handleProfileUpload}
                                />

                                <button
                                    type="button"
                                    onClick={triggerProfileUpload}
                                    className="absolute bottom-0 right-0 bg-[#D9D9D9] rounded-full p-2"
                                >
                                    <FiCamera className="w-4 h-4 text-black" />
                                </button>
                            </div>
                        </div>

                        {/* Text Fields */}
                        <input
                            type="text"
                            name="name"
                            placeholder="Name"
                            value={form.name}
                            onChange={handleChange}
                            className="w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] 
               bg-[#F5F9FE] dark:bg-white 
               text-black dark:text-black
               border border-gray-200 dark:border-0"
                        />
                        <div
                            className=""
                        >
                            <textarea
                                name="bio"
                                placeholder="Write a short description (max 100 words). You can include links to groups, communities, products, or external websites."
                                value={form.bio}
                                onChange={handleChange}
                                rows={3}
                                className="w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] 
       bg-[#F5F9FE] dark:bg-white 
       text-black dark:text-black
       border border-gray-200 dark:border-0"
                            />
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                {form.bio.split(/\s+/).length} / 100 words
                            </p>
                        </div>

                        <input
                            type="text"
                            name="website"
                            placeholder="Website Link"
                            value={form.website}
                            onChange={handleChange}
                            onPaste={(e) => {
                                // Let the paste happen naturally, then format if needed
                                setTimeout(() => {
                                    const pastedText = e.target.value;
                                    // Optional: Add https:// if missing
                                    if (pastedText && !pastedText.match(/^https?:\/\//)) {
                                        setForm(prev => ({ ...prev, website: `https://${pastedText}` }));
                                    }
                                }, 0);
                            }}
                            className="w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] 
               bg-[#F5F9FE] dark:bg-white 
               text-black dark:text-black
               border border-gray-200 dark:border-0"
                        />

                        {/* Niche Selector */}
                        <div>
                            <label htmlFor="niche" className="sr-only">Niche</label>
                            <select
                                id="niche"
                                name="niche"
                                value={form.niche}
                                onChange={handleChange}
                                className="w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] 
               bg-[#F5F9FE] dark:bg-white 
               text-black dark:text-black
               border border-gray-200 dark:border-0 appearance-none bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGNsYXNzPSJsdWNpZGUgbHVjaWRlLWNoZXZyb24tZG93biI+PHBhdGggZD0ibTYgOSA2IDYgNi02Ii8+PC9zdmc+')] bg-no-repeat bg-[position:right_0.5rem_center] bg-[size:1.5em_1.5em]"
                            >
                                <option disabled selected>Select your niche</option>
                                <option value="Filmmaker">Filmmaker</option>
                                <option value="Content Creator">Content Creator</option>
                                <option value="Drone Operator">Drone Operator</option>
                                <option value="Gaffer">Gaffer</option>
                                <option value="Sound Designer">Sound Designer</option>
                                <option value="VFX Artist">VFX Artist</option>
                                <option value="Editor">Editor</option>
                            </select>
                        </div>
                        {/* Country Selector */}
                        <div>
                            <Select
                                id="country"
                                name="country"
                                options={countryOptions}
                                value={countryOptions.find(option => option.value === form.country)}
                                onChange={(selectedOption) =>
                                    handleChange({ target: { name: 'country', value: selectedOption.value } })
                                }
                                placeholder="Select your country"
                                isSearchable
                                styles={customSelectStyles}
                            />
                        </div>

                        <textarea
                            name="about"
                            placeholder="About Section"
                            value={form.about}
                            onChange={handleChange}
                            rows={8}
                            className="w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] 
               bg-[#F5F9FE] dark:bg-white 
               text-black dark:text-black
               border border-gray-200 dark:border-0"
                        />

                        {/* Showreel Upload */}
                        <div
                            className="w-full cursor-pointer border-dashed border-2 border-gray-400 p-6 text-center rounded-xl bg-[#F5F9FE] text-[#7C8BA0]"
                            onClick={triggerVideoUpload}
                        >
                            <input
                                ref={videoInputRef}
                                type="file"
                                accept="video/mp4,video/mov"
                                className="hidden"
                                onChange={(e) => {
                                    setForm({ ...form, video: e.target.files[0], portfolioBanner: null });
                                }}
                            />
                            <img src={ICONS.upload} className="mx-auto mb-2 w-8 h-8" alt="Upload" />
                            <p className="text-sm md:max-w-56 mx-auto">
                                Click to upload your show-reel video to display on your profile. Recommended 16:9, max 30 seconds (.mp4 or .mov)
                            </p>
                        </div>
                        {!form.video && currentUser?.video && (

                            <button
                                onClick={handleDeleteVideo}
                                disabled={isSubmitting || isDeleting}
                                className="w-full bg-red-600 text-white py-2 rounded-xl font-medium"
                            >
                                {isDeleting ? "Deleting Video..." : "Delete Showreel Video"}
                            </button>
                        )}
                        {/* Video Preview */}
                        {form.video && (
                            <div className="relative mt-1">
                                <video
                                    src={URL.createObjectURL(form.video)}
                                    controls
                                    className="w-full rounded-lg"
                                />
                                <button
                                    onClick={() => setForm({ ...form, video: null })}
                                    className="absolute top-2 right-2 bg-red-500 rounded-full p-1"
                                >
                                    <FiX className="w-4 h-4" />
                                </button>
                            </div>
                        )}

                        {/* Portfolio Section */}
                        <div
                            className="w-full cursor-pointer border-dashed border-2 border-gray-400 p-6 text-center rounded-xl bg-[#F5F9FE] text-[#7C8BA0]"
                            onClick={triggerPortfolioUpload}
                        >
                            <div className=" items-center justify-center">
                                <input
                                    ref={portfolioInputRef}
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                            const imageUrl = URL.createObjectURL(file);
                                            setTempImage(imageUrl);  // Show crop modal
                                            setShowCropModal(true);
                                        }
                                    }}
                                />
                                <img src={ICONS.upload} className="mx-auto mb-2 w-8 h-8" alt="Upload" />
                                <p className="text-sm md:max-w-56 mx-auto">
                                    Or upload 1 portfolio image to display on your profile. Recommended 16:9 (.png, .jpg, .jpeg)
                                </p>
                            </div>
                        </div>
                        {!form.portfolioBanner && currentUser?.portfolioBanner && (

                            <button
                                onClick={handleDeletePortfolioBanner}
                                disabled={isSubmitting || isDeleting}
                                className="w-full bg-red-600 text-white py-2 rounded-xl font-medium"
                            >
                                {isDeleting ? "Deleting Image..." : "Delete Portfolio Image"}
                            </button>
                        )}
                        {form.portfolioBanner && (
                            <div className="relative mt-3">
                                <img
                                    src={URL.createObjectURL(form.portfolioBanner)}
                                    alt="Portfolio Banner"
                                    className="w-full h-48 object-cover rounded-lg"
                                />
                                <button
                                    onClick={() => setForm({ ...form, portfolioBanner: null })}
                                    className="absolute top-2 right-2 bg-red-500 rounded-full p-1"
                                >
                                    <FiX className="w-4 h-4 text-white" />
                                </button>
                            </div>
                        )}


                        <button
                            onClick={handleSave}
                            className="bg-primary text-white text-lg font-semibold py-3 rounded-xl w-full mt-4"
                        >
                            {isSubmitting ? uploadProgress > 0
                                ? `Uploading Video… ${uploadProgress}%` : "Saving..." : "Save Profile"}
                        </button>
                    </div>

                    {showCropModal && tempImage && (
                        <ImageCropModal
                            image={tempImage}
                            aspect={16 / 9}
                            onCancel={() => setShowCropModal(false)}
                            onCropComplete={handleCroppedImage}
                        />
                    )}
                </main>
            </div>
        </div>
    );
};

export default EditProfile;
